// Elastic Audio, gerechnete Fassung (Pro Tools: Rendered Processing).
// Gedehnte bzw. tonhöhenverschobene Clips werden in Stücken zu 6 s gerechnet (im Hintergrund, mehrere Worker).
// Jedes Stück hängt nur an der Zeitkarte in seinem Fenster: Trimmen und Teilen rechnen nichts neu, ein Warp-Marker
// nur die Stücke, die er berührt. Die Stücke werden mit kurzen Überblendungen zu einem Puffer je Clip zusammengesetzt.
import type { Clip, Project, Track } from '@srs/shared';
import { engine } from './engine.ts';
import { setElasticResolver } from './segments.ts';
import { pointMap, renderStretch, type StretchMode, type TuneData } from './stretch.ts';
import { tuneCurve, yin, type PitchTrack } from '../edit/pitch.ts';
import { breakpoints, clipMap, isElastic, needsRender } from '../edit/warp.ts';

const CHUNK = 6, MARGIN = 0.25, XF = 0.02;
type Chunk = { k: number; wa: number; wb: number; key: string };
const chunks = new Map<string, Float32Array[]>();
const pending = new Map<string, Promise<void>>();
const assembled = new Map<string, string>();
const listeners = new Set<() => void>();
export const onElasticChange = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const emit = () => listeners.forEach((l) => l());

// ---- Worker-Pool ----
interface Job { channels: Float32Array[]; sampleRate: number; points: { out: number; src: number }[]; outLen: number; mode: StretchMode; semitones?: number; tune?: TuneData }
let pool: Worker[] | null = null, rr = 0, seq = 0;
const waiting = new Map<number, (d: { channels?: Float32Array[]; pitch?: PitchTrack }) => void>();
function workers(): Worker[] {
  if (!pool) {
    const n = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
    pool = Array.from({ length: n }, () => {
      const w = new Worker(new URL('./stretch.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e) => { const f = waiting.get(e.data.id); waiting.delete(e.data.id); f?.(e.data); };
      w.onerror = (e) => { console.error('Elastic-Worker:', e.message); };
      return w;
    });
  }
  return pool;
}
/** Dehnen/Tonhöhe im Hintergrund rechnen (auch für AudioSuite) */
export function runJob(j: Job): Promise<Float32Array[]> {
  if (typeof Worker === 'undefined') return Promise.resolve(renderStretch({ ...j, srcPos: pointMap(j.points) }));
  const ws = workers(), id = ++seq;
  return new Promise((res) => { waiting.set(id, (d) => res(d.channels!)); ws[rr++ % ws.length].postMessage({ id, ...j }, j.channels.map((c) => c.buffer as ArrayBuffer)); });
}

// ---- Tonhöhe je Quelle (Vocal-Tuning), einmal berechnet ----
const pitchCache = new Map<string, PitchTrack>(), pitchBusy = new Map<string, Promise<PitchTrack | null>>();
export const pitchOfNow = (sourceId: string) => pitchCache.get(sourceId) ?? null;
/** Tonhöhenverlauf (YIN, je 10 ms) einer Quelle; im Worker gerechnet */
export function pitchOf(sourceId: string): Promise<PitchTrack | null> {
  const have = pitchCache.get(sourceId);
  if (have) return Promise.resolve(have);
  const busy = pitchBusy.get(sourceId);
  if (busy) return busy;
  const buf = engine.buffers.get(sourceId);
  if (!buf) return Promise.resolve(null);
  const mono = new Float32Array(buf.length);
  for (let c = 0; c < buf.numberOfChannels; c++) { const d = buf.getChannelData(c); for (let i = 0; i < mono.length; i++) mono[i] += d[i] / buf.numberOfChannels; }
  const job = (typeof Worker === 'undefined'
    ? Promise.resolve(yin(mono, buf.sampleRate))
    : new Promise<PitchTrack>((res) => { const ws = workers(), id = ++seq; waiting.set(id, (d) => res(d.pitch!)); ws[rr++ % ws.length].postMessage({ id, kind: 'pitch', mono, sampleRate: buf.sampleRate }, [mono.buffer]); }))
    .then((tr) => { pitchCache.set(sourceId, tr); pitchBusy.delete(sourceId); emit(); return tr; });
  pitchBusy.set(sourceId, job);
  return job;
}
const tuneSig = (c: Clip, sA: number, sB: number) => c.tune ? JSON.stringify([c.tune.drift, c.tune.vibrato, c.tune.speed, c.tune.humanize ?? 0, c.tune.notes.filter((n) => n.s1 > sA - 0.5 && n.s0 < sB + 0.5).map((n) => [n.s0.toFixed(3), n.s1.toFixed(3), n.to.toFixed(3), n.off ? 1 : 0])]) : '';

// ---- Schlüssel ----
const fnv = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); };
function chunkList(p: Project, c: Clip, mode: StretchMode): Chunk[] {
  const m = clipMap(p, c), out: Chunk[] = [], end = c.start + c.duration;
  for (let k = Math.floor(c.start / CHUNK); k * CHUNK < end - 1e-9; k++) {
    const wa = k * CHUNK - MARGIN, wb = (k + 1) * CHUNK + MARGIN;
    const pts = breakpoints(p, c, wa, wb);
    for (let i = 0; i <= 12; i++) pts.push(wa + ((wb - wa) * i) / 12);
    const sig = pts.sort((a, b) => a - b).map((t) => `${t.toFixed(5)}:${m.toSrc(t).toFixed(6)}`).join(',');
    out.push({ k, wa, wb, key: fnv(`${c.sourceId}|${mode}|${c.warp?.pitch ?? 0}|${k}|${sig}|${tuneSig(c, m.toSrc(wa), m.toSrc(wb))}`) });
  }
  return out;
}

/** Stück rechnen (einmal je Schlüssel) */
function renderChunk(p: Project, c: Clip, ch: Chunk, mode: StretchMode): Promise<void> {
  const have = pending.get(ch.key);
  if (have) return have;
  const buf = engine.buffers.get(c.sourceId!);
  if (!buf) return Promise.resolve();
  const sr = buf.sampleRate, m = clipMap(p, c);
  const bps = breakpoints(p, c, ch.wa, ch.wb);
  const srcs = bps.map((t) => m.toSrc(t));
  // nur den nötigen Ausschnitt der Quelle schicken (plus Spielraum für die Fenster)
  const s0 = Math.max(0, Math.floor((Math.min(...srcs) - 0.3) * sr)), s1 = Math.min(buf.length, Math.ceil((Math.max(...srcs) + 0.3) * sr));
  const channels = Array.from({ length: buf.numberOfChannels }, (_, i) => buf.getChannelData(i).slice(s0, Math.max(s0 + 1, s1)));
  const points = bps.map((t, i) => ({ out: (t - ch.wa) * sr, src: srcs[i] * sr - s0 }));
  const tuneData = async (): Promise<TuneData | undefined> => {
    if (!c.tune?.notes.length) return undefined;
    const tr = await pitchOf(c.sourceId!);
    if (!tr) return undefined;
    // Kurve über die ganze Quelle, dann den Ausschnitt dieses Stücks
    const curve = tuneCurve(tr, c.tune.notes, c.tune), hopS = tr.hop * sr;
    const k0 = Math.max(0, Math.floor((s0 / sr - tr.off) / tr.hop) - 1), k1 = Math.min(tr.f0.length, Math.ceil((s1 / sr - tr.off) / tr.hop) + 2);
    // Rahmen k deckt [Mitte − hop/2, Mitte + hop/2)
    return { f0: tr.f0.slice(k0, k1), shift: curve.slice(k0, k1), hopS, origin: (tr.off + k0 * tr.hop) * sr - hopS / 2 - s0 };
  };
  const job = tuneData().then((tune) => runJob({ channels, sampleRate: sr, points, outLen: Math.round((ch.wb - ch.wa) * sr), mode, semitones: c.warp?.pitch, tune }))
    .then((out) => { chunks.set(ch.key, out); pending.delete(ch.key); emit(); });
  pending.set(ch.key, job);
  return job;
}

/** Fertige Stücke zu einem Puffer für den Clip zusammensetzen (Überblendung an den Stückgrenzen) */
function assemble(c: Clip, list: Chunk[], sr: number, nch: number): string {
  const key = `el:${fnv(`${c.start}|${c.duration}|${list.map((x) => x.key).join(',')}`)}`;
  if (assembled.has(key)) return key;
  const len = Math.max(1, Math.round(c.duration * sr)), out = new AudioBuffer({ length: len, numberOfChannels: nch, sampleRate: sr });
  for (let ci = 0; ci < nch; ci++) {
    const dst = out.getChannelData(ci);
    for (let i = 0; i < len; i++) {
      const t = c.start + i / sr, k = Math.floor(t / CHUNK), local = t - k * CHUNK;
      const read = (kk: number) => { const ch = list.find((x) => x.k === kk); const d = ch && chunks.get(ch.key)?.[Math.min(ci, (chunks.get(ch.key)?.length ?? 1) - 1)]; if (!d || !ch) return 0; const j = Math.round((t - ch.wa) * sr); return d[j] ?? 0; };
      let v = read(k);
      if (local < XF && list.some((x) => x.k === k - 1)) { const w = local / XF; v = v * w + read(k - 1) * (1 - w); }
      dst[i] = v;
    }
  }
  engine.buffers.set(key, out);
  assembled.set(key, key);
  return key;
}

/** Was spielt ein Clip? null = normal (1:1), 'pending' = wird gerechnet, sonst der gerechnete Puffer */
export function resolveElastic(p: Project, c: Clip): { id: string; src0: number } | 'pending' | null {
  const t = p.tracks.find((x) => x.id === c.trackId);
  if (!isElastic(t) || !c.warp || !c.sourceId || !needsRender(p, c)) return null;
  const buf = engine.buffers.get(c.sourceId);
  if (!buf) return 'pending';
  const mode = (t as Track).elastic!, list = chunkList(p, c, mode);
  if (list.every((x) => chunks.has(x.key))) return { id: assemble(c, list, buf.sampleRate, buf.numberOfChannels), src0: 0 };
  for (const ch of list) if (!chunks.has(ch.key)) void renderChunk(p, c, ch, mode);
  return 'pending';
}
/** Alles Nötige rechnen (vor Export und Konsolidieren) */
export async function elasticReady(p: Project): Promise<void> {
  for (let i = 0; i < 50; i++) {
    const open = p.clips.filter((c) => c.kind === 'audio' && resolveElastic(p, c) === 'pending');
    if (!open.length) return;
    await Promise.all([...pending.values()]);
    if (!pending.size) await new Promise((r) => setTimeout(r, 20));
  }
}
/** Wird gerade etwas gerechnet? (für die Anzeige) */
export const elasticBusy = () => pending.size;

setElasticResolver(resolveElastic);
